import { useMoneyFormatter } from "@/lib/money-format";
/**
 * /companies/:companyId/reportes/iva
 * Reporte de IVA view.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Printer, Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { IvaReportView } from "@/components/reports/IvaReportView";
import { PrintableReport } from "@/components/reports/PrintableReport";
import { ReportPeriodPicker, type PeriodRange } from "@/components/reports/ReportPeriodPicker";
import { CsvExportButton } from "@/components/reports/CsvExportButton";
import { useActiveCompany } from "@/lib/company-context";
import { useIvaReport } from "@/lib/api/reports.queries";

export const Route = createFileRoute("/companies/$companyId/reportes/iva")({
  component: IvaPage,
});

function currentMonthRange(): PeriodRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(lastDay).padStart(2, "0")}` };
}

function IvaPage() {
  return (
    <RequireAuth>
      <AppShell>
        <IvaContent />
      </AppShell>
    </RequireAuth>
  );
}

function IvaContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const [period, setPeriod] = useState<PeriodRange>(currentMonthRange);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data: report, isLoading, isError, refetch } = useIvaReport(companyId, {
    periodFrom: period.from,
    periodTo: period.to,
  });

  const formatMoney = useMoneyFormatter("decimal");

  const csvRows = report
    ? [
        ...report.generated.items.map((i) => ({
          tipo: "Generado",
          documento: i.documentId,
          clase: i.documentType,
          tercero: i.thirdPartyName,
          base: formatMoney(i.baseAmount),
          iva: formatMoney(i.taxAmount),
          fecha: i.date,
        })),
        ...report.descontable.items.map((i) => ({
          tipo: "Descontable",
          documento: i.documentId,
          clase: i.documentType,
          tercero: i.thirdPartyName,
          base: formatMoney(i.baseAmount),
          iva: formatMoney(i.taxAmount),
          fecha: i.date,
        })),
      ]
    : [];

  return (
    <PrintableReport
      title={t("reports.iva.title")}
      subtitle={report ? `${report.period.from} — ${report.period.to}` : undefined}
    >
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-amber-600" />
            <h1 className="text-xl font-bold">{t("reports.iva.title")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <CsvExportButton
              filename={`reporte-iva-${period.from}-${period.to}`}
              rows={csvRows}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="mr-2 h-4 w-4" />
              {t("reports.print")}
            </Button>
          </div>
        </div>

        <div className="print:hidden">
          <ReportPeriodPicker value={period} onChange={setPeriod} />
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : isError ? (
          <ErrorState
            title={t("reports.error.title")}
            description={t("reports.error.body")}
            onRetry={() => void refetch()}
          />
        ) : report ? (
          <IvaReportView report={report} />
        ) : null}
      </div>
    </PrintableReport>
  );
}
